import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  RotateCcw,
  LayoutDashboard,
  ExternalLink,
  AlertTriangle,
  BarChart3,
  Wrench,
  FileText,
} from 'lucide-react';
import type { OnboardingState, RuleResult } from '../../types';
import ExecutiveTab from './dashboard/ExecutiveTab';
import RemediationTab from './dashboard/RemediationTab';
import RuleResultsTab from './dashboard/RuleResultsTab';

interface Props {
  state: OnboardingState;
  onReset: () => void;
}

const DQ_UI_BASE = process.env.REACT_APP_DQ_UI_URL || 'http://localhost:3000';

type TabId = 'executive' | 'remediation' | 'rules';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'executive', label: 'Executive', icon: BarChart3 },
  { id: 'remediation', label: 'Remediation', icon: Wrench },
  { id: 'rules', label: 'Rule Results', icon: FileText },
];

// ─── Fallback: basic results view (when sdq_report is null) ───

function getScoreColor(pct: number): string {
  if (pct >= 90) return 'text-cargo-success';
  if (pct >= 70) return 'text-orange-400';
  return 'text-cargo-error';
}

function getScoreBg(pct: number): string {
  if (pct >= 90) return 'bg-cargo-success/10 border-cargo-success/30';
  if (pct >= 70) return 'bg-orange-400/10 border-orange-400/30';
  return 'bg-cargo-error/10 border-cargo-error/30';
}

function formatRuleName(name: string): string {
  return name
    .replace(/^expect_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function StatCard({
  label,
  value,
  className = 'text-cargo-text',
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6 text-center">
      <p className="text-xs font-semibold text-cargo-muted uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className={`text-3xl font-bold ${className}`}>{value}</p>
    </div>
  );
}

// ─── Main Component ───

const ResultsPage: React.FC<Props> = ({ state, onReset }) => {
  const navigate = useNavigate();
  const result = state.createAndRunResult;
  const [activeTab, setActiveTab] = useState<TabId>('executive');

  // Fallback if no result yet
  if (!result) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-10 h-10 text-cargo-warning mx-auto mb-4" />
        <h2 className="text-xl font-bold text-cargo-text mb-2">No Results Available</h2>
        <p className="text-cargo-muted mb-6">Something went wrong. Please try again.</p>
        <button
          onClick={onReset}
          className="flex items-center gap-2 mx-auto bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-6 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Start Over
        </button>
      </div>
    );
  }

  const validation = result.validation_results;
  const stats = validation?.statistics;
  const successPct = stats?.success_percent ?? 0;
  const hasValidation = result.validation_status === 'completed' && validation;
  const sdqReport = result.sdq_report;
  const hasDashboard = !!sdqReport;

  const dqUiUrl = `${DQ_UI_BASE}/projects/${result.project_id}/creategroup/${result.group_id}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <CheckCircle2 className="w-6 h-6 text-cargo-success" />
          <h2 className="text-2xl font-bold text-cargo-text">Assessment Complete</h2>
        </div>
        <p className="text-cargo-muted">
          Project <strong className="text-cargo-text">{result.project_name}</strong> —{' '}
          <strong className="text-cargo-text">{result.rules_count}</strong> rules
        </p>
      </div>

      {/* Validation failed warning */}
      {result.validation_status === 'failed' && (
        <div className="bg-cargo-warning/10 border border-cargo-warning/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-cargo-warning shrink-0 mt-0.5" />
          <div>
            <p className="text-cargo-text font-medium text-sm">GE Validation could not complete</p>
            <p className="text-cargo-muted text-sm mt-1">
              {result.validation_error || 'The PDQConsumer Server may not be running.'}
              {hasDashboard
                ? ' The SDQ assessment ran successfully using local analysis.'
                : ' You can run validation from the DQ UI.'}
            </p>
          </div>
        </div>
      )}

      {/* ═══ Rich Dashboard (when sdq_report available) ═══ */}
      {hasDashboard && (
        <>
          {/* Tab bar */}
          <div className="flex gap-1 bg-cargo-surface border border-cargo-border rounded-lg p-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all
                  ${activeTab === id
                    ? 'bg-cargo-accent text-white shadow-sm'
                    : 'text-cargo-muted hover:text-cargo-text hover:bg-cargo-hover/50'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div>
            {activeTab === 'executive' && <ExecutiveTab report={sdqReport} />}
            {activeTab === 'remediation' && <RemediationTab report={sdqReport} />}
            {activeTab === 'rules' && hasValidation && <RuleResultsTab results={validation!} />}
            {activeTab === 'rules' && !hasValidation && (
              <div className="text-center py-8">
                <p className="text-cargo-muted">No validation results available.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══ Fallback: basic results view (no sdq_report) ═══ */}
      {!hasDashboard && hasValidation && (
        <>
          {/* DQ Score + Stats row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`border rounded-xl p-6 text-center ${getScoreBg(successPct)}`}>
              <p className="text-xs font-semibold text-cargo-muted uppercase tracking-wider mb-1">
                DQ Score
              </p>
              <p className={`text-4xl font-bold ${getScoreColor(successPct)}`}>
                {successPct.toFixed(1)}%
              </p>
            </div>
            <StatCard label="Total Rules" value={stats!.evaluated_expectations} />
            <StatCard label="Passed" value={stats!.successful_expectations} className="text-cargo-success" />
            <StatCard label="Failed" value={stats!.unsuccessful_expectations} className="text-cargo-error" />
          </div>

          {/* Per-rule results table */}
          {validation!.rule_results.length > 0 && (
            <div className="bg-cargo-surface border border-cargo-border rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-cargo-border">
                <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider">
                  Rule Results
                </h3>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cargo-panel sticky top-0">
                    <tr className="text-left text-cargo-muted">
                      <th className="px-6 py-3 font-medium">Status</th>
                      <th className="px-6 py-3 font-medium">Column</th>
                      <th className="px-6 py-3 font-medium">Rule</th>
                      <th className="px-6 py-3 font-medium text-right">Unexpected</th>
                      <th className="px-6 py-3 font-medium text-right">Unexpected %</th>
                      <th className="px-6 py-3 font-medium text-right">Total Rows</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cargo-border">
                    {validation!.rule_results.map((rr: RuleResult, i: number) => (
                      <tr key={i} className="hover:bg-cargo-hover/30 transition-colors">
                        <td className="px-6 py-3">
                          {rr.success ? (
                            <span className="inline-flex items-center gap-1.5 text-cargo-success">
                              <CheckCircle2 className="w-4 h-4" /> PASS
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-cargo-error">
                              <XCircle className="w-4 h-4" /> FAIL
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-cargo-text font-mono text-xs">{rr.column || '—'}</td>
                        <td className="px-6 py-3 text-cargo-text">{formatRuleName(rr.rule_name)}</td>
                        <td className="px-6 py-3 text-right text-cargo-text tabular-nums">{rr.unexpected_count.toLocaleString()}</td>
                        <td className="px-6 py-3 text-right text-cargo-text tabular-nums">{rr.unexpected_percent.toFixed(2)}%</td>
                        <td className="px-6 py-3 text-right text-cargo-muted tabular-nums">{rr.element_count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-6 rounded-lg transition-colors"
        >
          <LayoutDashboard className="w-4 h-4" />
          Home
        </button>
        <a
          href={dqUiUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-cargo-panel hover:bg-cargo-hover text-cargo-text
                     font-medium py-2.5 px-6 rounded-lg transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Manage in DQ UI
        </a>
        <button
          onClick={onReset}
          className="flex items-center gap-2 bg-cargo-panel hover:bg-cargo-hover text-cargo-text
                     font-medium py-2.5 px-6 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Start Over
        </button>
      </div>
    </div>
  );
};

export default ResultsPage;
